import { Component, inject, OnInit } from "@angular/core";
import { NgIf, NgFor, NgClass, AsyncPipe, JsonPipe, UpperCasePipe } from "@angular/common";

import { IonApp, IonRouterOutlet } from "@ionic/angular/standalone";

import { environment } from "src/environments/environment";

import { HelperDebugLoggerControllerService, DebugLog } from "./helpers/helper-debug-logger-controller.service";
import { HelperSessionControllerService } from "./helpers/helper-session-controller.service";
import { HelperBiometricAuthControllerService } from "./helpers/helper-biometric-auth-controller.service";
import { HelperThemeControllerService } from "./helpers/helper-theme-controller.service";
import { HelperNavControllerService } from "./helpers/helper-nav-controller.service";
import { HelperAnalyticsControllerService } from "./helpers/helper-analytics-controller.service";
import { HelperPushNotificationControllerService } from "./helpers/helper-push-notification-controller.service";
import { HelperCrashReportingControllerService } from "./helpers/helper-crash-reporting-controller.service";
import { HelperDeepLinkControllerService } from "./helpers/helper-deep-link-controller.service";
import { HelperNetworkSyncControllerService } from "./helpers/helper-network-sync-controller.service";
import { ROUTE_DIRECTION, ROUTE_PATH_GLOBAL } from "./types/route.type";

@Component({
  selector: "app-root",
  templateUrl: "app.component.html",
  styleUrls: ["app.component.scss"],
  standalone: true,
  imports: [NgIf, NgFor, NgClass, AsyncPipe, JsonPipe, UpperCasePipe, IonApp, IonRouterOutlet],
})
export class AppComponent implements OnInit {
  allowNetworkDrawer = environment.allowNetworkDrawer;
  isDrawerOpen = false;
  copiedMessage: string | null = null;
  latestOtpValue: string | null = null;

  public debugLogger = inject(HelperDebugLoggerControllerService);
  public sessionController = inject(HelperSessionControllerService);
  public biometricAuthService = inject(HelperBiometricAuthControllerService);
  public themeService = inject(HelperThemeControllerService);
  private analyticsService = inject(HelperAnalyticsControllerService);
  private pushNotificationService = inject(HelperPushNotificationControllerService);
  private crashReportingService = inject(HelperCrashReportingControllerService);
  private deepLinkService = inject(HelperDeepLinkControllerService);
  public networkSyncService = inject(HelperNetworkSyncControllerService);
  private helperNavControllerService = inject(HelperNavControllerService);

  constructor() {
    this.debugLogger.latestOtp$.subscribe(otp => {
      this.latestOtpValue = otp;
    });
  }

  async ngOnInit(): Promise<void> {
    this.crashReportingService.init();
    this.deepLinkService.init();
    this.networkSyncService.init();
    this.analyticsService.init();
    this.pushNotificationService.init();
  }

  private showToast(message: string): void {
    this.copiedMessage = message;
    setTimeout(() => {
      this.copiedMessage = null;
    }, 2000);
  }

  async toggleBiometricLock(): Promise<void> {
    if (this.biometricAuthService.isBiometricsEnabled()) {
      this.biometricAuthService.disableBiometrics();
      this.showToast('Biometric lock disabled');
    } else {
      const enabled = await this.biometricAuthService.enableBiometrics();
      if (enabled) {
        this.showToast('Biometric lock enabled');
      } else {
        this.showToast('Biometric authentication failed');
      }
    }
  }

  toggleDrawer(): void {
    this.isDrawerOpen = !this.isDrawerOpen;
  }

  closeDrawer(): void {
    this.isDrawerOpen = false;
  }

  openBuildInfo(): void {
    this.closeDrawer();
    this.helperNavControllerService.navigate(ROUTE_PATH_GLOBAL.BuildInfo, ROUTE_DIRECTION.Forward);
  }

  clearLogs(): void {
    this.debugLogger.clearLogs();
  }

  copyLatestOtp(): void {
    if (this.latestOtpValue) {
      this.copyText(this.latestOtpValue);
    }
  }

  copyText(text: string | null | undefined): void {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      this.showToast('Copied to clipboard!');
    });
  }

  trackByLogId(_index: number, log: DebugLog): string {
    return log.id;
  }
}
